/**
 * Shared mailboxes — addresses like info@ or support@ that several people read and answer.
 *
 * Talks to mail-service /v1/mail/admin (a shared mailbox is a group on the mail server, its
 * members are people's own mailboxes). Members see it under their own mailbox in Eurion Mail
 * and answer as its address; nobody needs a separate password.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Inbox, Plus, Trash2, Pencil, Loader2, AlertTriangle, Search, X, Users } from 'lucide-react';

export interface SharedMailbox { id: string; address: string; displayName: string; members: string[] }
interface MailUser { email: string; name: string }
interface ListView { configured: boolean; domains: string[]; mailboxes: SharedMailbox[] }

const BASE = '/v1/mail/admin';

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, init);
  const body = await res.json().catch(() => ({})) as { success?: boolean; data?: T; error?: { message?: string } };
  if (!res.ok || body.success === false) throw new Error(body.error?.message ?? `HTTP ${res.status}`);
  return body.data as T;
}
const send = <T,>(method: string, path: string, data?: unknown) => api<T>(path, {
  method, ...(data !== undefined ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) } : {}),
});

/** Checkbox list of people with a mailbox, with a filter. */
export function MemberPicker({ users, selected, onChange }: { users: MailUser[]; selected: string[]; onChange: (v: string[]) => void }) {
  const [filter, setFilter] = useState('');
  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return f ? users.filter(u => u.email.includes(f) || u.name.toLowerCase().includes(f)) : users;
  }, [users, filter]);
  const toggle = (email: string) => onChange(selected.includes(email) ? selected.filter(e => e !== email) : [...selected, email].sort());
  return (
    <div className="rounded-lg border border-nord-2">
      <div className="flex items-center gap-2 border-b border-nord-2 px-3 py-2">
        <Search className="h-4 w-4 text-nord-4" aria-hidden="true" />
        <input aria-label="Filter people" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter people"
          className="flex-1 bg-transparent text-sm text-nord-6 outline-none placeholder-nord-4/60" />
        <span className="text-xs text-nord-4">{selected.length} selected</span>
      </div>
      <ul className="max-h-56 overflow-y-auto py-1" aria-label="People">
        {shown.length === 0 && <li className="px-3 py-2 text-sm text-nord-4">No one matches.</li>}
        {shown.map(u => (
          <li key={u.email}>
            <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-nord-5 hover:bg-nord-2/60">
              <input type="checkbox" checked={selected.includes(u.email)} onChange={() => toggle(u.email)} />
              <span className="truncate">{u.name ? <>{u.name} <span className="text-nord-4">· {u.email}</span></> : u.email}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SharedMailboxes() {
  const [view, setView] = useState<ListView | null>(null);
  const [users, setUsers] = useState<MailUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Create form
  const [creating, setCreating] = useState(false);
  const [local, setLocal] = useState('');
  const [domain, setDomain] = useState('');
  const [name, setName] = useState('');
  const [members, setMembers] = useState<string[]>([]);
  // Edit / delete
  const [editing, setEditing] = useState<SharedMailbox | null>(null);
  const [editMembers, setEditMembers] = useState<string[]>([]);
  const [editName, setEditName] = useState('');
  const [deleting, setDeleting] = useState<SharedMailbox | null>(null);
  const [confirmText, setConfirmText] = useState('');

  const load = useCallback(async () => {
    try {
      const v = await api<ListView>('/shared-mailboxes');
      setView(v);
      setDomain(d => d || v.domains[0] || '');
      if (v.configured) setUsers((await api<{ users: MailUser[] }>('/mail-users')).users);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const run = async (fn: () => Promise<void>, done: string) => {
    setBusy(true); setError(null); setNotice(null);
    try { await fn(); setNotice(done); await load(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const create = () => run(async () => {
    await send('POST', '/shared-mailboxes', { address: `${local.trim()}@${domain}`, displayName: name, members });
    setCreating(false); setLocal(''); setName(''); setMembers([]);
  }, `${local.trim()}@${domain} created.`);

  const saveEdit = () => editing && run(async () => {
    await send('PATCH', `/shared-mailboxes/${encodeURIComponent(editing.id)}`, { displayName: editName, members: editMembers });
    setEditing(null);
  }, `${editing.address} saved.`);

  const remove = () => deleting && run(async () => {
    await send('DELETE', `/shared-mailboxes/${encodeURIComponent(deleting.id)}`);
    setDeleting(null); setConfirmText('');
  }, `${deleting.address} deleted.`);

  if (!view && !error) return <div className="p-8 text-sm text-nord-4"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div>;

  return (
    <div className="max-w-3xl space-y-5 p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-nord-6">Shared mailboxes</h1>
          <p className="mt-1 text-sm text-nord-4">
            Addresses like info@ or support@ that several people read and answer. Members find the mailbox under their own in
            Eurion Mail and reply as its address. Sent replies stay in the mailbox, so everyone sees what was answered.
          </p>
        </div>
        {view?.configured && !creating && (
          <button type="button" onClick={() => { setCreating(true); setNotice(null); }}
            className="flex flex-shrink-0 items-center gap-2 rounded-lg bg-nord-8 px-3 py-1.5 text-sm font-medium text-nord-0 hover:bg-nord-8/90">
            <Plus className="h-4 w-4" /> New shared mailbox
          </button>
        )}
      </div>

      {error && <div role="alert" className="rounded-xl border border-nord-11/40 bg-nord-11/10 p-4 text-sm text-nord-5">{error}</div>}
      {notice && <div role="status" className="rounded-xl border border-nord-8/40 bg-nord-8/10 p-4 text-sm text-nord-5">{notice}</div>}

      {view && !view.configured && (
        <div className="flex items-start gap-3 rounded-xl border border-nord-13/40 bg-nord-13/10 p-4 text-sm text-nord-5">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-nord-13" />
          Shared mailboxes are not set up on this server yet. They become available after the next update of the mail server settings.
        </div>
      )}

      {creating && view && (
        <form className="space-y-4 rounded-xl border border-nord-2 bg-nord-1 p-5" aria-label="New shared mailbox"
          onSubmit={e => { e.preventDefault(); void create(); }}>
          <div>
            <label htmlFor="sm-local" className="mb-1 block text-xs font-medium text-nord-4">Address</label>
            <div className="flex items-center gap-1">
              <input id="sm-local" required value={local} onChange={e => setLocal(e.target.value.toLowerCase())} placeholder="info"
                pattern="[a-z0-9]([a-z0-9._\-]*[a-z0-9])?" maxLength={64}
                className="w-40 rounded-lg border border-nord-3 bg-nord-0 px-3 py-1.5 text-sm text-nord-6" />
              <span className="text-sm text-nord-4">@</span>
              {view.domains.length > 1 ? (
                <select aria-label="Domain" value={domain} onChange={e => setDomain(e.target.value)}
                  className="rounded-lg border border-nord-3 bg-nord-0 px-2 py-1.5 text-sm text-nord-6">
                  {view.domains.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              ) : <span className="text-sm text-nord-6">{domain}</span>}
            </div>
          </div>
          <div>
            <label htmlFor="sm-name" className="mb-1 block text-xs font-medium text-nord-4">Name (optional)</label>
            <input id="sm-name" value={name} onChange={e => setName(e.target.value)} placeholder="Customer service" maxLength={200}
              className="w-full rounded-lg border border-nord-3 bg-nord-0 px-3 py-1.5 text-sm text-nord-6" />
          </div>
          <div>
            <div className="mb-1 text-xs font-medium text-nord-4">Members</div>
            <MemberPicker users={users} selected={members} onChange={setMembers} />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setCreating(false)} className="rounded-lg border border-nord-3 px-3 py-1.5 text-sm text-nord-5 hover:bg-nord-2">Cancel</button>
            <button type="submit" disabled={busy || !local.trim()} className="rounded-lg bg-nord-8 px-3 py-1.5 text-sm font-medium text-nord-0 disabled:opacity-50">
              {busy ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {view?.configured && view.mailboxes.length === 0 && !creating && (
        <div className="rounded-xl border border-dashed border-nord-3 p-8 text-center text-sm text-nord-4">
          <Inbox className="mx-auto mb-2 h-8 w-8" /> No shared mailboxes yet.
        </div>
      )}

      <ul className="space-y-3" aria-label="Shared mailboxes">
        {view?.mailboxes.map(m => (
          <li key={m.id} className="rounded-xl border border-nord-2 bg-nord-1 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate font-medium text-nord-6">{m.address}</div>
                {m.displayName && <div className="text-sm text-nord-4">{m.displayName}</div>}
              </div>
              <div className="flex flex-shrink-0 gap-1">
                <button type="button" aria-label={`Edit ${m.address}`} onClick={() => { setEditing(m); setEditMembers(m.members); setEditName(m.displayName); setDeleting(null); }}
                  className="rounded p-1.5 text-nord-4 hover:bg-nord-2 hover:text-nord-6"><Pencil className="h-4 w-4" /></button>
                <button type="button" aria-label={`Delete ${m.address}`} onClick={() => { setDeleting(m); setConfirmText(''); setEditing(null); }}
                  className="rounded p-1.5 text-nord-4 hover:bg-nord-2 hover:text-nord-11"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <Users className="h-4 w-4 text-nord-4" aria-hidden="true" />
              {m.members.length === 0 && <span className="text-sm text-nord-4">No members: nobody can read this mailbox.</span>}
              {m.members.map(e => <span key={e} className="rounded-full bg-nord-2 px-2.5 py-0.5 text-xs text-nord-5">{e}</span>)}
            </div>

            {editing?.id === m.id && (
              <form className="mt-4 space-y-3 border-t border-nord-2 pt-4" aria-label={`Edit ${m.address}`} onSubmit={e => { e.preventDefault(); void saveEdit(); }}>
                <div>
                  <label htmlFor="sm-edit-name" className="mb-1 block text-xs font-medium text-nord-4">Name</label>
                  <input id="sm-edit-name" value={editName} onChange={e => setEditName(e.target.value)} maxLength={200}
                    className="w-full rounded-lg border border-nord-3 bg-nord-0 px-3 py-1.5 text-sm text-nord-6" />
                </div>
                <MemberPicker users={users} selected={editMembers} onChange={setEditMembers} />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setEditing(null)} className="rounded-lg border border-nord-3 px-3 py-1.5 text-sm text-nord-5 hover:bg-nord-2">Cancel</button>
                  <button type="submit" disabled={busy} className="rounded-lg bg-nord-8 px-3 py-1.5 text-sm font-medium text-nord-0 disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
                </div>
              </form>
            )}

            {deleting?.id === m.id && (
              <div className="mt-4 space-y-3 rounded-lg border border-nord-11/40 bg-nord-11/10 p-4" role="alertdialog" aria-label={`Delete ${m.address}`}>
                <p className="text-sm text-nord-5">
                  Deleting <strong>{m.address}</strong> also deletes <strong>all mail in it</strong>, and mail sent to the address will bounce.
                  Type the address to confirm.
                </p>
                <div className="flex items-center gap-2">
                  <input aria-label="Type the address to confirm" value={confirmText} onChange={e => setConfirmText(e.target.value)}
                    className="flex-1 rounded-lg border border-nord-3 bg-nord-0 px-3 py-1.5 text-sm text-nord-6" />
                  <button type="button" onClick={() => setDeleting(null)} className="rounded p-1.5 text-nord-4 hover:bg-nord-2" aria-label="Cancel"><X className="h-4 w-4" /></button>
                  <button type="button" disabled={busy || confirmText.trim().toLowerCase() !== m.address} onClick={() => void remove()}
                    className="rounded-lg bg-nord-11 px-3 py-1.5 text-sm font-medium text-nord-6 disabled:opacity-40">{busy ? 'Deleting…' : 'Delete'}</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
